<template>
    <div>
        <b-card no-body>
            <div class="m-2">
                <div>
                    <b-row>
                        <b-col cols="12">
                            <h1 class="float-left">{{ i18nT(`Purchase invoices`) }}</h1>

                            <b-dropdown
                                v-if="iCan( 'payments', 'write' )"
                                id="add-new-dropdown"
                                variant="primary"
                                class="float-right"
                                right
                            >
                                <template #button-content>
                                    <feather-icon icon="PlusCircleIcon" />
                                    {{ i18nT(`Add new`) }}
                                </template>

                                <b-dropdown-item>
                                    <b-link :to="{ name: 'create-expense' }">
                                        <font-awesome-icon class="mr-25" :icon="['fas', 'file-invoice-dollar']" />
                                        {{ i18nT(`Expense`) }}
                                    </b-link>
                                </b-dropdown-item>

                                <b-dropdown-item v-b-toggle.sidebar-import-invoices>
                                    <b-link >
                                        <font-awesome-icon class="mr-25" :icon="['fas', 'cloud-upload-alt']" />
                                        {{ i18nT(`Bulk upload`) }}
                                    </b-link>
                                </b-dropdown-item>

                              <b-dropdown-item
                                  v-b-modal.modal-import-csv
                              >
                                <div class="text-primary">
                                  <font-awesome-icon class="mr-25" :icon="['fas', 'file-import']" />
                                  {{ i18nT(`Import`) }}
                                </div>
                              </b-dropdown-item>

                                <b-dropdown-item
                                        v-b-modal.modal-export
                                        :disabled="totalDocuments == 0"
                                    >
                                    <div :class="{'text-primary': totalDocuments !== 0}">
                                        <font-awesome-icon class="mr-25 ml-25" :icon="['fas', 'file-export']" />
                                        {{ i18nT(`Export`) }}
                                    </div>
                                </b-dropdown-item>

                            </b-dropdown>
                        </b-col>
                    </b-row>


                    <!-- CHART AND REPORTING -->
                    <b-row v-if="showExpenseSummary && documents.length > 0" >
                        <b-col cols="12" md="4">
                            <vue-apex-charts
                                v-if="report && report.currency"
                                id="expense-summary-chart"
                                type="donut"
                                height="300"
                                :series="series"
                                :options="chartOptions"
                            >

                            </vue-apex-charts>
                        </b-col>
                        <b-col
                            cols="12" md="6"
                            class="budget-wrapper"
                        >
                            <b-row>
                                <b-col cols="6">
                                    <h4>{{i18nT(`Expenses`)}}</h4>
                                    <div class="mt-1 pb-1 mb-2 border-bottom">
                                        <h5>{{i18nT(`Total expenses`)}}</h5>
                                        <h3 v-if="expenseSummaryValue('total_invoiced')" class="mb-25 text-success font-weight-bolder">
                                            {{ {amount: parseFloat(expenseSummaryValue('total_invoiced')).toFixed(2), currency: currency} | currency }}
                                        </h3>
                                        <h4 v-else class="text-muted">
                                            {{i18nT(`No data available`)}}
                                        </h4>
                                    </div>

                                    <div v-for="chartTotal in sideSeries.actual" :key="chartTotal.name" class="mt-1">
                                        <h5 v-if="chartItemHasData(chartTotal)">{{chartTotal.name}}</h5>
                                        <h3 v-if="chartItemHasData(chartTotal)" class="mb-25" :class="`text-`+chartTotal.color">
                                            {{ {amount: parseFloat(chartTotal.data).toFixed(2), currency: currency} | currency }}
                                        </h3>
                                    </div>


                                </b-col>
                                <b-col cols="6">
                                    <h4>{{i18nT(`Balance`)}}</h4>
                                    <div class="mt-1 pb-1 mb-2 border-bottom">
                                        <h5>{{i18nT(`Not paid`)}}</h5>
                                        <h3 v-if="report.total_not_paid" class="mb-25 text-danger font-weight-bolder">
                                            {{ {amount: parseFloat(report.total_not_paid).toFixed(2), currency: currency} | currency }}
                                        </h3>
                                        <h4 v-else class="text-muted">
                                            {{i18nT(`No data available`)}}
                                        </h4>
                                    </div>


                                    <div v-for="chartTotal in sideSeries.remaining" :key="chartTotal.name" class="mt-1">
                                        <h5 v-if="chartItemHasData(chartTotal)">{{chartTotal.name}}</h5>
                                        <h3
                                            v-if="chartItemHasData(chartTotal)"  class="mb-25" :class="`text-`+chartTotal.color">
                                            {{ {amount: chartTotal.data, currency: currency} | currency }}
                                        </h3>

                                    </div>
                                </b-col>
                            </b-row>
                        </b-col>
                    </b-row>

                    <div class="mb-1">
                        <span class="text-muted">{{ i18nT(`Showing`) }} {{
                                dataMeta.from
                            }} {{ i18nT(`to`) }} {{ dataMeta.to }} {{ i18nT(`of`) }} {{
                                dataMeta.of
                            }} {{ i18nT(`entries`) }}</span>
                    </div>
                    <b-row>
                        <b-col
                            cols="12"
                            md="3"
                        >
                            <b-form-group
                                :label="i18nT(`Reporting period`)"
                                label-for="paymentType"
                                v-if="controlVisible"
                            >
                                <b-input-group>
                                    <b-input-group-prepend>
                                        <b-button
                                            variant="primary"
                                            @click.stop="inputClick"
                                        >
                                            <font-awesome-icon  :icon="['fas', 'calendar-alt']" />
                                        </b-button>
                                    </b-input-group-prepend>
                                    <VueCtkDateTimePicker
                                        :id="'ExpenseDateRange'"
                                        class=""
                                        :range="true"
                                        :min-date="minDate"
                                        :disabled="false"
                                        v-model="reportRange"
                                        :only-date="true"
                                        :only-time="false"
                                        :format="`YYYY-MM-DD`"
                                        :formatted="dateFormat"
                                        :color="`#5a5aff`"
                                        :label="i18nT(`Select date`)"
                                        :button-now-translation="i18nT(`Today`)"
                                        :first-day-of-week="1"
                                        :no-button="true"
                                        :auto-close="true"
                                        :locale="locale"
                                        :ref="`reportRangePicker`"
                                    />
                                </b-input-group>

                            </b-form-group>
                        </b-col>

                        <!-- Vendors -->
                        <b-col
                            cols="12"
                            md="3"
                            v-if="displayExtentControls && controlVisible"
                        >
                            <b-form-group
                                :label="i18nT(`Client`)"
                                label-for="assignee"
                            >
                                <v-select
                                    id="assignee"
                                    v-model="vendor"
                                    :options="vendors"
                                    :clearable="false"
                                    :reduce="opt=> parseInt(opt.value)"
                                    label="text"
                                />
                            </b-form-group>
                        </b-col>


                        <b-col
                            cols="12"
                            md="3"
                        >
                            <b-form-group
                                :label="i18nT(`Category`)"
                                label-for="paymentType"
                                v-if="controlVisible"
                            >
                                <v-select
                                    id="paymentType"
                                    v-model="paymentCategory"
                                    :options="paymentCategoryList"
                                    :reduce="opt=> opt.value"
                                    label="text"
                                    :clearable="false"
                                />
                            </b-form-group>
                        </b-col>

                        <b-col
                            cols="12"
                            md="3"
                        >
                            <b-form-group
                                :label="i18nT(`Payment method`)"
                                label-for="paymentType"
                                v-if="controlVisible"
                            >
                                <v-select
                                    id="software"
                                    v-model="paymentType"
                                    :options="paymentTypes"
                                    :reduce="opt=> parseInt(opt.value)"
                                    :clearable="false"
                                    label="text"
                                />
                            </b-form-group>
                        </b-col>

                        <b-col
                            cols="12"
                            md="3"
                        >
                            <b-form-group
                                :label="i18nT(`Payment status`)"
                                label-for="payment-status"
                                v-if="controlVisible"
                            >
                                <v-select
                                    id="payment-status"
                                    v-model="invoiceStatus"
                                    :options="selectPaymentStatuses"
                                    :clearable="false"
                                    :reduce="opt=> opt.value"
                                    label="text"
                                    :disabled="publicationStatus == 1"
                                />
                            </b-form-group>
                        </b-col>



                        <b-col
                            cols="12"
                            md="3"
                            class="pt-1"
                        >
                            <b-form-checkbox name="drafts-only" class="mt-2" switch
                                             :value="1"
                                             v-model="attachment">
                                {{ i18nT(`Only without attachments`) }}
                            </b-form-checkbox>

                        </b-col>

                    </b-row>
                </div>
            </div>

            <div>
                <div>
                    <div
                        class="row mx-2"
                        v-if="displayExtentControls"
                    >
                        <div class="col-12 col-md-6 col-sm 6 mb-1 pl-0">
                            <v-select
                                v-model="perPage"
                                :options="perPageOptions"
                                :clearable="false"
                                class="per-page-selector d-inline-block"
                            />
                            <b-dropdown
                                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                                :text="i18nT(`Actions`)"
                                size="md"
                                class="d-inline ml-1"
                                variant="outline-secondary"
                                v-if="selectedItems.length > 0"
                            >
                                <b-dropdown-item
                                    v-if="iCan( 'payments', 'write' )"
                                    @click="onBulkMarkPaid">
                                    <feather-icon icon="CheckIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Mark as Paid`) }}</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-b-toggle.sidebar-bulk-send
                                    v-if="iCan( 'payments', 'write' )"
                                    @click="onBulkSendPerEmail">
                                    <feather-icon icon="MailIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Send per e-mail`) }}</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="iCan( 'payments', 'write' )"
                                    @click="onBulkDelete">
                                    <feather-icon
                                        icon="TrashIcon"
                                        color="red"
                                    />
                                    <span
                                        class="align-middle ml-50"
                                        style="color: red"
                                    >{{ i18nT(`Delete`) }}</span>
                                </b-dropdown-item>
                            </b-dropdown>

                        </div>
                        <!-- search input -->
                        <div class="col-12 col-md-6 col-sm 6 mb-1 pr-0">
                            <div class="custom-search d-flex justify-content-end">
                                <b-form-group>
                                    <div
                                        role="group"
                                        class="input-group input-group-merge"
                                    >
                                        <div class="input-group-prepend">
                                            <div class="input-group-text">
                                                <feather-icon icon="SearchIcon"/>
                                            </div>
                                        </div>
                                        <b-form-input
                                            v-model.lazy="searchQuery"
                                            :placeholder="i18nT(`Search`)"
                                            type="text"
                                            class="d-inline-block"
                                        />
                                    </div>
                                </b-form-group>
                            </div>
                        </div>
                    </div>
                    <b-table
                        ref="refDocumentListTable"
                        :items="fetchDocuments"
                        responsive
                        :fields="computedColumns"
                        primary-key="id"
                        :sort-by.sync="sortBy"
                        show-empty
                        :empty-html="emptyHtml"
                        :sort-desc.sync="isSortDirDesc"
                        class="position-relative"
                        @refreshed="onTableRefresh"
                        v-model="currentItems"
                    >

                        <template v-slot:head(checkbox)="data">
                            <span>{{ data.label.toUpperCase() }}</span>
                            <b-form-checkbox
                                class="d-inline-block pl-0"
                                @change="onSelectAll"
                                v-model="selectAllStatus"
                            >
                            </b-form-checkbox>
                        </template>

                        <template #head(action)="data">
                            <span>{{ data.label.toUpperCase() }}</span>
                            <div class="sk-column-control">
                                <b-dropdown
                                    no-caret
                                    variant="button"
                                    toggle-class="p-0"
                                    :right="true"
                                    class="pr-1 pl-1 d-flex sk-column-filter"
                                    v-b-tooltip = "i18nT(`Customize columns`)"
                                >
                                    <template #button-content>
                                        <feather-icon
                                            icon="SettingsIcon"
                                            size="18"
                                            class="text-right"
                                        />
                                    </template>

                                    <div class="pl-1 pr-1" v-for="(field, index) in avalableFields" :key="'Fields - ' + index">
                                        <b-form-checkbox class="mb-1 mt-1 text-nowrap" v-model="field.visible">{{ i18nT(field.label) }}</b-form-checkbox>
                                    </div>
                                </b-dropdown>
                            </div>
                        </template>

                        <template #cell(checkbox)="data">
                            <b-form-checkbox
                                v-model="selectedItems"
                                :value="data.item.Id"
                            />
                        </template>

                        <template #cell(number)="data">


                            <div v-if="data.item.VendorId" class="media">
                                <div class="media-aside align-self-center">
                                    <b-link
                                        :to="{name: 'edit-vendor', params: { id: data.item.VendorObj.Id }}"
                                        class="font-weight-bold d-block text-nowrap"
                                    >
                                        <b-avatar
                                            :to="{
                                              name: 'edit-vendor',
                                              params: { id: data.item.VendorId },
                                            }"
                                            :src="data.item.VendorObj.ImageUrl"
                                            :text="(data.item.VendorObj.Label ? vendorInitials(data.item.VendorObj.Label) : i18nT(`Expense without vendor`))"
                                            class="badge-light-primary"
                                            :badge-variant="data.item.InvoiceFileId ? '' : 'warning'"
                                            badge-offset="-2px"
                                        >
                                            <template #badge v-if="!data.item.InvoiceFileId"><span v-b-tooltip.hover.right="i18nT(`Missing expense document`)">!</span></template>
                                        </b-avatar>
                                    </b-link>
                                </div>
                                <div class="media-body">
                                    <b-link
                                        :class="data.item.PaidStatus == 'void' ? 'opacity-08' : ''"
                                        :to="{ name: 'edit-expense', params: { id: data.item.Id } }"
                                    >
                                        <span v-if="data.item.Prefix">{{ data.item.Prefix}}{{ data.item.InvoiceNumber }}</span>
                                        <span v-else>{{ data.item.InvoiceNumber }}</span>
                                        <p class="font-small-2 mb-0 text-primary">{{ getCategoryName(data.item.Category) }}</p>
                                    </b-link>

                                    <b-link
                                        v-if="data.item.VendorId"
                                        :to="{ name: 'expense-preview', params: { id: data.item.Id } }"
                                        class="font-weight-bold d-block text-nowrap font-small-2"
                                    >
                                        {{ data.item.VendorObj.Label | truncate(20) }}
                                    </b-link>

                                    <span class="text-warning font-small-2" v-if="!data.item.InvoiceFileId">
                                        {{i18nT(`Expense document missing`)}}
                                    </span>


                                </div>
                                <div class="d-flex justify-content-start align-items-center">
                                    <span class="cursor-pointer d-inline-block text-primary mr-1"
                                          @click="previewInModal(data)"
                                          v-if="data.item.InvoiceFileId"
                                          v-b-tooltip="i18nT(`Preview expense`)">
                                        <font-awesome-icon
                                            class="mr-25" :icon="['fas', 'eye']" />
                                    </span>

                                    <span class="cursor-pointer d-inline-block text-primary mr-1"
                                          @click="attachInModal(data)"
                                          v-b-tooltip="i18nT(`Attach expense document`)"
                                        v-else
                                    >
                                        <font-awesome-icon
                                            class="mr-25" :icon="['fas', 'upload']" />
                                    </span>

                                </div>
                            </div>


                        </template>

                        <template #cell(payment_type)="data">
                            <span v-if="data.item.PaymentTypeObj"
                                  :class="data.item.PaidStatus == 'void' ? 'opacity-08' : ''">
                                {{ data.item.PaymentTypeObj.label }}
                            </span>
                        </template>

                        <template #cell(vendor)="data">
                            <span :class="data.item.PaidStatus == 'void' ? 'opacity-08' : ''"
                                v-if="data.item.VendorObj">{{ data.item.VendorObj.Label }}</span>
                        </template>

                        <template #cell(total_amount)="data">
                            <span :class="data.item.Type == 1 ? 'text-success' : 'text-danger'">
                                {{data.item.Type == 1 ? '+' : '-'}}{{ {amount: data.item.TotalAmount, currency: data.item.Currency } | currency }}
                            </span>
                        </template>


                        <template #cell(date)="data">
                            <span :class="data.item.PaidStatus == 'void' ? 'opacity-08' : ''">
                                {{ data.item.InvoiceDate | date }}
                            </span>
                        </template>

                        <template #cell(due_date)="data">
                            <div class="text-center">
                                <span v-if="data.item.DueDate" :class="data.item.PaidStatus == 'void' ? 'opacity-08' : ''">
                                    {{ data.item.DueDate | date }}
                                </span>
                                <span v-else class="align-text-center text-muted" >
                                    N/A
                                </span>
                            </div>
                        </template>

                        <template #cell(PaidStatus)="data">

                            <b-badge v-if="data.item.PublicationStatus == 'DRAFT'"
                                class="mr-1 badge-pill">
                                {{i18nT(`Draft`)}}
                            </b-badge>

                            <b-badge
                                v-else
                                :variant="statusLabelClass(data.item.PaidStatus)"
                                class="mr-1 badge-pill">
                                {{statusLabel(data.item.PaidStatus)}}
                            </b-badge>

                            <div v-if="data.item.SentAt" class="mt-05">
                                <span class="font-weight-bold d-block text-nowrap text-muted font-small-2">{{ i18nT(`Sent on`) }}: {{ data.item.SentAt | date }}</span>
                            </div>
                            <div v-if="data.item.IsPaid && data.item.InvoicePayments[0]" class="mt-05">
                                <span class="font-weight-bold d-block text-nowrap text-muted font-small-2">{{ i18nT(`Paid on`) }}: {{ data.item.InvoicePayments[0].PaymentDate | date }}</span>
                            </div>

                        </template>


                        <template #cell(DueAmount)="data">
                            <div v-if="data.item.PaidAmount" :class="`text-`+statusLabelClass(data.item.PaidStatus)">
                                <span class="font-small-3">{{i18nT(`Paid`)}}:</span> {{ {amount: data.item.PaidAmount, currency: data.item.Currency } | currency }}
                            </div>
                            <div :class="`text-`+statusLabelClass(data.item.PaidStatus)">
                               <span class="font-small-3">{{i18nT(`Due`)}}:</span> {{ {amount: data.item.DueAmount, currency: data.item.Currency } | currency }}
                            </div>
                        </template>


                        <template #cell(action)="data">
                            <b-dropdown
                                variant="link"
                                toggle-class="p-0"
                                no-caret
                                right
                            >

                                <template #button-content>
                                    <feather-icon
                                        icon="MoreVerticalIcon"
                                        size="16"
                                        class="align-middle text-body"
                                    />
                                </template>

                                <b-dropdown-item :to="{ name: 'expense-preview', params: { id: data.item.Id } }">
                                    <feather-icon icon="EyeIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Preview`) }}</span>
                                </b-dropdown-item>

                                <b-dropdown-item :to="{ name: 'edit-expense', params: { id: data.item.Id } }">
                                    <feather-icon icon="EditIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Edit`) }}</span>
                                </b-dropdown-item>

                                <b-dropdown-item :href="'/invoice/' + data.item.Id + '/h/' + data.item.Hash + '?lang=' + locale" target="_blank">
                                    <feather-icon icon="LinkIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Open public link`) }}</span>
                                </b-dropdown-item>

                                <b-dropdown-item :href="data.item.InvoiceDownloadUrl + '&lang=' + locale" target="_blank">
                                    <feather-icon icon="DownloadIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Download`) }}</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="data.item.PaidStatus !== 'paid' && data.item.PaidStatus !== 'overpaid' && data.item.RecordType !== 3"
                                    @click="setPaymentInvoiceObj(data.item)">
                                    <feather-icon icon="PlusIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Add payment manually`) }}</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="data.item.PaidStatus !== 'paid' && data.item.PaidStatus !== 'overpaid' && data.item.RecordType !== 3"
                                    @click="matchToTransaction(data.item)">
                                    <feather-icon icon="CreditCardIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Match to transaction`) }}</span>
                                </b-dropdown-item>

                                <b-dropdown-item
                                    v-if="iCan( 'payments', 'write' )"
                                    @click="onSingleCopy(data.item)">
                                    <feather-icon icon="CopyIcon"/>
                                    <span class="align-middle ml-50">{{ i18nT(`Copy`) }}</span>
                                </b-dropdown-item>
                                <!-- <b-dropdown-item @click="onCopy(data)">
                                  <feather-icon icon="EditIcon" />
                                  <span class="align-middle ml-50">{{i18nT(`Edit`)}}</span>
                                </b-dropdown-item> -->
                                <b-dropdown-item
                                    v-if="data.item.PaidStatus === 'not_paid'"
                                    @click="toggleVoid(data.item.Id)">
                                    <feather-icon
                                        icon="ArchiveIcon"
                                        color="red"
                                    />
                                    <span
                                        class="align-middle ml-50"
                                        style="color: red"
                                    >{{ i18nT(`Mark as Void`) }}</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="data.item.PaidStatus === 'void'"
                                    @click="toggleVoid(data.item.Id)">
                                    <feather-icon
                                        icon="CornerUpLeftIcon"
                                        color="red"
                                    />
                                    <span
                                        class="align-middle ml-50"
                                        style="color: red"
                                    >{{ i18nT(`Mark as Not Paid`) }}</span>
                                </b-dropdown-item>


                                <b-dropdown-item
                                    v-if="data.item.PaidStatus !== 'paid' && data.item.PaidStatus !== 'partial'"
                                    @click="onDelete(data)">
                                    <feather-icon
                                        icon="TrashIcon"
                                        color="red"
                                    />
                                    <span
                                        class="align-middle ml-50"
                                        style="color: red"
                                    >{{ i18nT(`Delete`) }}</span>
                                </b-dropdown-item>
                            </b-dropdown>
                        </template>
                    </b-table>

                </div>

                <div class="mx-1 mb-2">
                    <b-row>
                        <!-- <b-col> -->
                        <!-- </b-col> -->
                        <b-col
                            cols="12"
                            sm="6"
                            class="d-flex align-items-center justify-content-center justify-content-sm-start"
                        >
                            <b-form-checkbox
                                class="d-inline-block mx-1"
                                @change="onSelectAll"
                                v-model="selectAllStatus"
                            >
                                {{ i18nT(`All`) }}
                            </b-form-checkbox>
                            <span class="text-muted">{{ i18nT(`Showing`) }} {{
                                    dataMeta.from
                                }} {{ i18nT(`to`) }} {{ dataMeta.to }} {{ i18nT(`of`) }} {{
                                    dataMeta.of
                                }} {{ i18nT(`entries`) }}</span>
                        </b-col>
                        <!-- Pagination -->
                        <b-col
                            cols="12"
                            sm="6"
                            class="d-flex align-items-center justify-content-center justify-content-sm-end"
                        >
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="totalDocuments"
                                :per-page="perPage"
                                first-number
                                last-number
                                class="mb-0 mt-1 mt-sm-0"
                                prev-class="prev-item"
                                next-class="next-item"
                            >
                                <template #prev-text>
                                    <feather-icon
                                        icon="ChevronLeftIcon"
                                        size="18"
                                    />
                                </template>
                                <template #next-text>
                                    <feather-icon
                                        icon="ChevronRightIcon"
                                        size="18"
                                    />
                                </template>
                            </b-pagination>

                        </b-col>

                    </b-row>
                </div>
            </div>
        </b-card>

      <invoice-sidebar-bulk-send-invoices @sent="refetchExpenseData" />
      <invoice-sidebar-add-payment
            :visible="isAddPaymentVisible"
            :invoice-id="invoiceData.Id"
            :due-amount="invoiceData.DueAmount ? parseFloat(invoiceData.DueAmount) : 0"
            :currency="invoiceData.Currency ? invoiceData.Currency : 'EUR'"
            :invoice-status="invoiceData.PaidStatus"
            :cur-locale="locale"
            @updated="refetchExpenseData"
            @close="closeAddPayment"
        />
      <b-modal
            id="preview-expense-modal"
            :title="i18nT(`Expense Preview`)"
            class="overflow-y-scroll"
            hide-footer
            size="lg"
            centered
        >
            <div class="frame-container">
                <iframe
                    :src="previewIframeUrl+'&lang='+locale"
                    width="100%"
                    height="60%"
                    frameborder="0"
                ></iframe>

                <div id="frame-spinner"
                     class="position-absolute
                                    w-100
                                    d-flex justify-content-center
                                    align-items-center align-content-center">
                    <b-spinner label="Loading..."
                               variant="primary"
                               class="mr-2"
                               color="primary"></b-spinner>
                </div>
            </div>
        </b-modal>
        <attach-expense-document-modal
            :visible="isAttachModalVisible"
            :expense="invoiceData"
            @change="isAttachModalVisible = $event"
            @close="isAttachModalVisible = false"
            @attached="handleAttachmentSuccess"
        />
      <!-- MODAL IMPORT CSV -->
      <modal-import-csv class-name="expenses" @imported="refetchData" :import-type="importType">
            <b-row class="pt-2">
                <b-col cols="12" md="3">
                    <h5>
                        {{ i18nT(`Select document type`) }}
                    </h5>
                </b-col>
                <b-col cols="12" md="9">
                    <v-select
                        v-model="importType"
                        :options="importOptions"
                        label="text"
                        class="pb-1"
                        :clearable="false"
                    />
                </b-col>
            </b-row>
        </modal-import-csv>

        <modal-export module="expenses" :allFilters="allFilters"/>

        <modal-invoice-transaction :invoice="invoiceData" @updated="refetchData"/>

        <sidebar-import-invoices @processed="refetchData"></sidebar-import-invoices>


    </div>
</template>

<script>
import {
    BLink,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormCheckbox,
    BFormInput,
    VBTooltip,
    VBToggle,
    BBadge,
    BInputGroupPrepend,
    BInputGroup, BSpinner, BSidebar, BAvatar,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import useExpenseList from './useExpenseList'
import {avatarText} from '@core/utils/filter'
import {dictToSelectArray} from '@core/utils/utils'
import router from '@/router'
import useAuth from '@/auth/useAuth'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
import moment from 'moment';
import InvoiceSidebarAddPayment from "@/views/apps/invoice/InvoiceSidebarAddPayment.vue";
import InvoiceSidebarBulkSendInvoices from "@/views/apps/invoice/InvoiceSidebarBulkSendInvoices.vue";
import SidebarImportInvoices from "@/views/components/sidebar/SidebarImportInvoices.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ModalImportCsv from "@/views/common/ModalImportCsv/ModalImportCsv.vue";
import ModalExport from "@/views/common/ModalExport/ModalExport.vue"
import ModalInvoiceTransaction from "@/views/accounting/Invoices/ModalInvoiceTransaction.vue"
import AttachExpenseDocumentModal from './components/AttachExpenseDocumentModal.vue'


export default {
    components: {
        BAvatar,
        ModalImportCsv,
        ModalExport,
        ModalInvoiceTransaction,
        BSpinner,
        BInputGroupPrepend,
        BBadge,
        BLink,
        BRow,
        BCol,
        BFormGroup,
        BCard,
        BButton,
        BTable,
        BDropdown,
        BDropdownItem,
        BPagination,
        vSelect,
        BFormCheckbox,
        BFormInput,
        VueApexCharts,
        VueCtkDateTimePicker,
        BInputGroup,
        InvoiceSidebarAddPayment,
        InvoiceSidebarBulkSendInvoices,
        SidebarImportInvoices,
        AttachExpenseDocumentModal,
    },
    directives: {
        Ripple,
        'b-tooltip': VBTooltip,
        'b-toggle': VBToggle,
    },
    data() {
        const displayExtentControls =
            useAuth.getCurrentSoftware() != 'ca' &&
            useAuth.getCurrentSoftware() != 'em'
        return {
            filterStatus: -1,
            paymentStatuses: [],
            invoiceData: {},
            previewIframeUrl: null,
            isAddPaymentVisible: false,
            isSendBulkBarVisible: false,
            isImportSidebarVsible: false,
            isAttachModalVisible: false,
            currency: 'EUR',
            report: {},
            reportRange: {
                start: this.startDate,
                end: this.endDate
            },
            invoiceTypes: [],
            paymentCategoryList: [],
            vendors: [],
            paymentTypes: [],
            pagination: {},
            selectedItems: [],
            currentItems: [],
            selectAllStatus: false,
            displayExtentControls,
            avalableFields: [
                {label: this.i18nT(`Expense date`), key: 'invoice_date', visible: true},
                {label: this.i18nT(`Due date`), key: 'due_date', visible: false},
                {label: this.i18nT(`Total Amount`), key: 'total_amount', visible: true},
                {label: this.i18nT(`Vendor`), key: 'vendor', visible: true},
                {label: this.i18nT(`Status`), key: 'PaidStatus', visible: true},
                {label: this.i18nT(`Open balance`), key: 'DueAmount', visible: true},
                {label: this.i18nT(`Payment type`), key: 'payment_type', visible: false},
            ],
            documentFilters: [
                {value: '', text: this.i18nT(`All`)},
                {value: 'created', text: this.i18nT(`Created by me`)},
                {value: 'shared', text: this.i18nT(`Shared by me`)}
            ],
            controlVisible: true,
            importOptions: [
                {
                    text: this.i18nT(`Expense`), module: 'finances', import_type: 4
                }

            ],
            importType: {},
            emptyHtml: "<img src='" + require('@/assets/images/pages/error.svg') + "' alt='Error page' class='img-fluid' width='300'><br/><br/><h4>"
            + this.i18nT(`Here, you can create and manage your expenses. No expenses have been created yet.`) + "</h4>"
            +"<button class='btn btn-outline-secondary mt-2'  onclick='window.location.href=\"/ac/finances/create/expense/\"'>" + this.i18nT(`Create expense`) + "</button>",
            showExpenseSummary: false,

        }
    },
    created() {

        let storePreferences = useAuth.getColumnPreferences('ExpenseTemplates')
        if(storePreferences) {
            this.avalableFields = storePreferences
        }

        this.importType = this.importOptions[0]


        //GET CATEGORIES
        this.$http.get(
            `invoices/categories?type=2`
        ).then(({data}) => {

            let validCategories = data.data;

            this.paymentCategoryList = validCategories.map(category => {
                return {
                    value: category.Id,
                    text: category.Label,
                    fullData: category
                }
            })
            this.paymentCategoryList.unshift({
                value: -1,
                text: this.i18nT(`Any`)
            })
        })

        //GET VENDORS
        this.$http.get(`vendors/vendorsList`).then(({data}) => {
            this.vendors = dictToSelectArray(data.data)
            this.vendors.unshift({
                value: -1,
                text: this.i18nT(`All`)
            })
        })

        //GET PAYMENT TYPES
        this.$http.get(`system/paymentTypes`).then(({data}) => {
            this.paymentTypes = dictToSelectArray(data.data)
            this.paymentTypes.unshift({
                value: -1,
                text: this.i18nT(`All`)
            })
        })

        //GET PAYMENT STATUSES

        this.$http.get(`invoices/paymentStatuses`).then(({data}) => {
            this.paymentStatuses = data.data;
        })



        //GET STATS SUMMARIES
        this.$http.get(`invoices/paymentsSummary?start_date=`+this.reportRange.start+`&type=2`).then(({ data }) => {
            this.report = data.data
            this.currency = data.data.currency
        })

    //   this.fetchDocuments(null, this.determineVisibility);

    },
    methods: {
        vendorInitials(vendorName) {
            if (!vendorName) return '';

            // Define known company suffixes (normalized to lowercase)
            const suffixes = [
                "ltd",
                "ltd.",
                "gmbh",
                "ood",
                "eood",
                "оод",
                "еоод",
                "ад",
                "ad",
                "еад",
                "ет",
                "inc",
                "inc.",
                "sarl",
                "eurl",
                "sas",
                "sa",
                "plc",
                "corp",
                "co",
                "llc",
                "pty",
                "pty.",
                "limited",
                "limited.",
                "company",
                "group",
                "ag"
            ];

            // Split the vendor name by spaces, and filter out any suffix words.
            const parts = vendorName.split(/\s+/);
            const filteredParts = parts.filter(part => {
                // Remove common punctuation and normalize to lowercase
                const cleaned = part.replace(/[\.,]/g, '').toLowerCase();
                return !suffixes.includes(cleaned);
            });

            // Re-join the remaining parts. If nothing remains, fall back to the original name.
            const cleanedName = filteredParts.join(' ') || vendorName;
            if (!cleanedName.length) return '';

            // Start with the first character of the cleaned name.
            let initials = cleanedName[0];

            // Look for any capital letters beyond the first character.
            for (let i = 1; i < cleanedName.length; i++) {
                const char = cleanedName[i];
                if (char === char.toUpperCase() && char !== char.toLowerCase()) {
                    initials += char;
                }
            }

            // Ensure there are at least two initials (if possible).
            if (initials.length === 1 && cleanedName.length > 1) {
                initials += cleanedName[1];
            }

            // Limit the initials to a maximum of 3 letters.
            if (initials.length > 3) {
                initials = initials.substring(0, 3);
            }

            return initials;
        },

        loadOldExpenses() {
            this.reportRange.start = moment(this.lastInvoiceDate).startOf('year').format('YYYY-MM-DD')
        },
        previewInModal(data) {
            this.previewIframeUrl = data.item.IframePublicUrl
            this.$bvModal.show('preview-expense-modal')
        },
        attachInModal(data) {
            this.invoiceData = data.item;
            this.isAttachModalVisible = true;
        },
        handleAttachmentSuccess() {
            this.refetchExpenseData()
        },
        chartItemHasData(chartTotal) {
            return typeof chartTotal.data != 'undefined' && chartTotal.data != 'NaN' && chartTotal.data !== '0.00'
        },
        expenseSummaryValue(key) {
            if (!this.report) return 0
            if (this.report[key]) return this.report[key]

            const expenseKey = key
                .replace('invoiced', 'expensed')
                .replace('invoice', 'expense')

            return this.report[expenseKey] || 0
        },
        refetchExpenseData(){
            this.refetchData()
            this.refetchSummaries()
        },
        refetchSummaries(){
            let filtersString = '';
            if(this.paymentType && this.paymentType.value != -1){
                filtersString += `&payment_type=${this.paymentType.value}`
            }
            if(this.vendor && this.vendor.value != -1){
                filtersString += `&vendor_id=${this.vendor.value}`
            }
            if(this.paymentCategory && this.paymentCategory.value != -1){
                filtersString += `&category_id=${this.paymentCategory.value}`
            }

            this.$http.get(`invoices/paymentsSummary?start_date=${this.startDate}&end_date=${this.endDate}&type=2`+filtersString).then(({ data }) => {
                this.report = data.data
                this.currency = data.data.currency
            })
        },
        toggleVoid(itemId) {
            this.$http
                .post(`invoices/toggleVoid?invoice_id=${itemId}`)
                .then(() => {
                    this.refetchData()
                    this.refetchSummaries()
                })
        },

        matchToTransaction(invoiceObj) {
            this.invoiceData = invoiceObj;
            this.$bvModal.show('modal-invoice-transaction');
        },

        setPaymentInvoiceObj(invoiceObj) {
            this.invoiceData = invoiceObj;
            this.openAddPaynmentModal();
        },



        openAddPaynmentModal() {
            if(this.isAddPaymentVisible) {
                this.isAddPaymentVisible = false;
            }
            setTimeout(() => {
                this.isAddPaymentVisible = true;
            }, 0);
        },
        closeAddPayment() {
            this.isAddPaymentVisible = false;
        },
        inputClick() {
            const inputElement = this.$refs[`reportRangePicker`].$el.querySelector('input');
            if (inputElement) {
                inputElement.click();
            }
        },
        getCategoryName(typeId) {
            const invoiceType = this.paymentCategoryList.find(type => type.value == typeId)
            return invoiceType ? invoiceType.text : ''
        },
        statusLabel(status){
            return this.hardCodedPaymentStatuses.find(
                paymentStatus => paymentStatus.value == status
            ).text
        },
        statusLabelClass(status) {
            return {
                'paid': 'success',
                'partial': 'warning',
                'not_paid': 'danger',
                'overdue': 'dark-danger',
                'overpaid': 'info',
                'void': 'light-dark',
            }[status]
        },
        getAssigneeName(assignee) {
            const assigneeList = Object.values(assignee)
            return assigneeList.map(ass => ass.Label).join(', ')
        },
        getAvatarName(name) {
            if (name) {
                return name
                    .split(' ')
                    .map(namePart => {
                        return namePart[0]
                    })
                    .join('')
                    .toUpperCase()
            } else {
                return ''
            }
        },
        onTableRefresh() {
            this.selectedItems = []
        },
        onCopy(element) {
            this.$http
                .post(`invoices/copy?invoice_id=${element.item.Id}`)
                .then(() => {
                    this.refetchData()
                })
        },
        onDelete(task) {
            this.$swal({
                title: this.i18nT(`Are you sure?`),
                text: this.i18nT(`You won't be able to revert this!`),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.i18nT(`Yes, delete it!`),
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-outline-danger ml-1'
                },
                buttonsStyling: false
            }).then(result => {
                if (result.value) {
                    this.$http.delete(`invoices?id=${task.item.Id}`).then(() => {
                        this.$swal({
                            icon: 'success',
                            title: this.i18nT(`Deleted!`),
                            text: this.i18nT(`The document has been deleted.`),
                            customClass: {
                                confirmButton: 'btn btn-success'
                            }
                        })
                        this.refetchData()
                        this.refetchSummaries()
                    }).catch(err => {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: this.errorMessage(err),
                                icon: 'AlertTriangleIcon',
                                variant: 'danger'
                            }
                        })
                    })
                }
            })
        },
        onSingleCopy(element) {
            this.$http
                .post(`invoices/copy?invoice_id=${element.Id}`)
                .then((resp) => {

                    this.$router.push({name: 'edit-expense', params: {id: resp.data.data.Id}});
                })
        },
        onBulkSendPerEmail() {
            this.$store.commit('invoices/SET_INVOICE_SET', this.selectedItems);
        },
        onBulkMarkPaid(){
            this.$swal({
                title: this.i18nT(`Mark all selected expenses as paid?`),
                text: this.i18nT(`To undo this action later you will have to manually remove payment records from each expense separately.`),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.i18nT(`Yes, mark as paid!`),
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-outline-danger ml-1'
                },
                buttonsStyling: false
            }).then(result => {
                if (result.value) {
                    let formData = new FormData();
                    this.selectedItems.forEach(item => {
                        formData.append('InvoiceIds[]', item);
                    });
                    formData.append('PaymentType', 'bank');
                    //formData.append('Description', '');
                    formData.append('PaymentDate', new Date().toISOString().split('T')[0]);

                    this.$http
                        .post(`invoices/invoicePayments`, formData)
                        .then(() => {
                            this.$swal({
                                icon: 'success',
                                title: this.i18nT(`Updated!`),
                                text:  this.i18nT(`Expenses marked as paid!`),
                                customClass: {
                                    confirmButton: 'btn btn-success'
                                }
                            })
                            this.selectedItems = []
                            this.selectAllStatus = false
                            this.refetchData()
                        })
                }
            })

        },
        onBulkDelete() {
            this.$swal({
                title: this.i18nT(`Are you sure?`),
                text: this.i18nT(`You won't be able to revert this!`),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.i18nT(`Yes, delete it!`),
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-outline-danger ml-1'
                },
                buttonsStyling: false
            }).then(result => {
                if (result.value) {
                    this.$http
                        .delete(`invoices?id=${this.selectedItems.join(',')}`)
                        .then(() => {
                            this.$swal({
                                icon: 'success',
                                title: this.i18nT(`Deleted!`),
                                text:  this.i18nT(`Selected expense(s) were deleted!`),
                                customClass: {
                                    confirmButton: 'btn btn-success'
                                }
                            })
                            this.selectedItems = []
                            this.selectAllStatus = false
                            this.refetchData()
                        })
                        .catch(err => {
                            this.$toast({
                                component: ToastificationContent,
                                position: 'top-right',
                                props: {
                                    title: this.errorMessage(err),
                                    icon: 'AlertTriangleIcon',
                                    variant: 'danger'
                                }
                            })
                        })
                }
            })
        },
        onSelectAll(val) {
            if (val) {
                this.selectedItems = this.currentItems.map(item => item.Id)
            } else {
                this.selectedItems = []
            }
        },
        onExportExcel() {
        },
        onExportCsv() {
        }
    },
    updated(){
        const leaveBtn = document.querySelector('#loadOldExpenses')
        if(leaveBtn) {
            leaveBtn.addEventListener('click', this.loadOldExpenses)
        }
    },
    watch: {
        documents: {
            handler: function(val) {

                if(val.length === 0 && this.lastInvoiceDate !== null) {
                    this.emptyHtml = "<img src='" + require('@/assets/images/pages/error.svg') + "' alt='Error page' class='img-fluid' width='300'><br/><br/><h4>"
                    + this.i18nT(`It seems you don't have any expenses for the selected period.<br/><br/>Click the button below to view the latest expenses.`) + "</h4>"
                    +"<button class='btn btn-warning mt-2' id='loadOldExpenses'>" + this.i18nT(`Load past expenses`) + "</button>"
                } else if (val.length === 0 && this.lastInvoiceDate === null) {
                    this.controlVisible = false
                }
            },
            deep: true
        },
        paymentCategory(newVal) {
            if (newVal) {
                this.refetchSummaries()
            }
        },
        paymentType(newVal) {
            if (newVal) {
                this.refetchSummaries()
            }
        },
        vendor(newVal) {
            if (newVal) {
                this.refetchSummaries()
            }
        },
        reportRange: {
            deep: true,
            handler(newRange) {
                if(newRange) {
                    if(newRange.start) {
                        this.startDate = moment(newRange.start).format('YYYY-MM-DD');
                        this.endDate = newRange.end ? moment(newRange.end).format('YYYY-MM-DD') : '';
                    }
                    this.refetchSummaries();
                } else {
                    this.startDate = '';
                    this.endDate = '';
                    this.refetchSummaries();
                }

            }
        },
    },
    setup() {
        const {
            fetchDocuments,
            tableColumns,
            perPage,
            currentPage,
            totalDocuments,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refDocumentListTable,
            statusFilter,
            invoiceType,
            invoiceSeries,
            recordType,
            assignedTo,
            invoiceStatus,
            publicationStatus,
            attachment,
            vendor,
            paymentType,
            paymentCategory,
            startDate,
            endDate,
            documents,
            documentFilter,
            lastInvoiceDate,
            refetchData
        } = useExpenseList(router.currentRoute.meta.template === 1)

        return {
            fetchDocuments,
            tableColumns,
            perPage,
            currentPage,
            totalDocuments,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refDocumentListTable,
            statusFilter,
            invoiceType,
            recordType,
            invoiceSeries,
            assignedTo,
            vendor,
            invoiceStatus,
            publicationStatus,
            attachment,
            paymentType,
            paymentCategory,
            startDate,
            endDate,
            avatarText,
            documents,
            documentFilter,
            lastInvoiceDate,
            refetchData
        }
    },
    computed: {
        selectPaymentStatuses() {
            let allStatuses = dictToSelectArray(this.paymentStatuses);
            allStatuses.unshift({
                value: -1,
                text: this.i18nT(`All`)
            });
            return allStatuses;
        },

        hardCodedPaymentStatuses() {
            return [
                {value: 'paid', text: this.i18nT(`Paid`)},
                {value: 'partial', text: this.i18nT(`Partial payment`)},
                {value: 'not_paid', text: this.i18nT(`Not paid`)},
                {value: 'overpaid', text: this.i18nT(`Overpaid`)},
                {value: 'void', text: this.i18nT(`Void`)},
                {value: 'overdue', text: this.i18nT(`Overdue`)}
            ]
        },

        computedColumns() {
            let col = [...this.tableColumns]
            for(let i = 0; i < this.avalableFields.length; i++) {
              if(!this.avalableFields[i].visible) {
                col = col.filter(c => c.key !== this.avalableFields[i].key)
              }
            }
            useAuth.setColumnPreferences('ExpenseTemplates', this.avalableFields);
            return col
        },
        dateFormat() {
            return useAuth.getDateFormat() !== 'undefined'
                ? useAuth.getDateFormat()
                : 'DD/MM/YYYY'
        },
        locale() {
            return useAuth.getLocale() !== 'undefined'
                ? useAuth.getLocale()
                : 'en'
        },
        minDate() {
            return moment('2022-01-01').format();
        },
        allFilters() {
            const params = {
                page: this.currentPage,
                show_per_page: this.perPage,
                sort_by: this.sortBy,
                sort_order: this.isSortDirDesc ? 'DESC' : 'ASC',
                search: this.searchQuery,
                start_date: this.startDate,
                end_date: this.endDate,
                export: 1,
            };

            params['filters[type]'] = 2;

            if (this.isTemplate) {
                params.templates = 1;
            }
            if (this.recordType.value != -1) {
                params['filters[record_type]'] = this.recordType.value;
            }
            if (this.vendor.value != -1) {
                params['filters[vendor]'] = this.vendor.value;
            }
            if (this.paymentType.value != -1) {
                params['filters[payment_type]'] = this.paymentType.value;
            }
            if (this.paymentCategory.value != -1) {
                params['filters[category]'] = this.paymentCategory.value;
            }
            if(this.startDate != ''){
                params['filters[start_date]'] = this.startDate;
            }
            if(this.endDate != ''){
                params['filters[end_date]'] = this.endDate;
            }

            return params
        },
        chartOptions() {
            // const categories = []
            let chartOptions = []
            //let colors = this.colors;

            chartOptions = {
                chart: {
                    type: 'donut',
                    height: '100%',
                    width: '100%'  // or any specific pixel value
                },
                labels: this.labels,
                dataLabels: {
                    enabled: true,
                    formatter: function(val) {
                        return val.toFixed(2)+"%";
                    },
                    style: {
                        colors: ['#fff'] // This will set the color of the percentage text. Change as needed.
                    }
                },
                colors: [
                    '#28c77a', //Paid
                    '#ff9800', //Partial
                    '#00cfe8', //Overpaid

                    '#ea5455', //Not paid
                    '#79082A', //Overdue
                    'rgba(185,185,195,0.6)', //Void

                ],

                responsive: [{
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom'
                        }
                    }
                }]
            }
            return chartOptions
        },
        sideSeries() {
            let sideSeries = {
                actual: [
                    {name: this.i18nT(`Paid in full`), data: parseFloat(this.report.total_paid).toFixed(2), color: 'success'},
                    {name: this.i18nT(`Overpaid`), data: parseFloat(this.report.total_overpaid).toFixed(2), color: 'info'},
                    {name: this.i18nT(`Partial payments`), data: parseFloat(this.report.total_partial).toFixed(2), color: 'warning'},
                ],
                remaining: [
                    {name: this.i18nT(`Overdue`), data: parseFloat(this.report.total_overdue).toFixed(2), color: 'dark-danger'},
                    {name: this.i18nT(`Void`), data: parseFloat(this.report.total_void).toFixed(2), color: 'muted'},
                ]
            }
            return sideSeries
        },

        series() {
            let series = []

            if (this.report) {

                    series = [
                        this.report.total_paid,
                        this.report.total_partial,
                        this.report.total_overpaid,
                        this.report.total_not_paid,
                        this.report.total_overdue,
                        this.report.total_void,
                    ]
            }
            return series
        },
        labels() {
            let labels = []

            if (this.report) {
                labels = [
                    this.i18nT(`Paid in full`),
                    this.i18nT(`Partial payment`),
                    this.i18nT(`Overpaid`),

                    this.i18nT(`Not paid`),
                    this.i18nT(`Overdue`),
                    this.i18nT(`Void`),
                ]
            }
            return labels
        }
    }

}
</script>

<style lang="scss">

.table-responsive > .table.b-table > thead > tr > .b-table-sticky-column {
    z-index: 3;
}

#preview-expense-modal.modal .modal-content {
    overflow: hidden;
    overflow-y: scroll;
}


.frame-container {
    position: relative;
    width: 100%;
    height: 820px;
    min-height: 720px;
}

.frame-container iframe {
    width: 100%;
    height: 100%;
    border: none;
    z-index: 20;
    position: relative;
}

#frame-spinner {
    display: block;
    z-index: 2;
    top: 50%;
}


.chart-color-1 {
    background-color: #28c77a;
}
.chart-color-2 {
    background-color: #1caf62;
}
.chart-color-3 {
    background-color: #ff2667;
}
.chart-color-4 {
    background-color: #C21146;
}

.chart-color-5 {
    background-color: #79082A;
}

div.date-time-picker {
    width: calc(100% - 56px);
}

.date-time-picker input.field-input {
    padding-right: 5px;
}

.ctk-date-time-picker__header,
.datetimepicker .custom-button,
.datetimepicker span.custom-button-content,
.datepicker-buttons-container .datepicker-button.now .datepicker-button-content
{
    /* Change the header text color */
    color: #5a5aff !important;
    fill: #5a5aff !important;
}

.sk-column-select {
    width: 180px;
}


.task-container {
    .task-item {
        padding-left: 1rem;
        padding-right: 1rem;
    }

    .line-item {
        margin-top: 4px;
    }
}
</style>
